import { baseApi } from "../api/baseApi";
import {
  SalesManagerFilterParams,
  SalesOverviewStats,
  SalespersonActivityStats,
  SalesTeamSummary,
} from "../types/salesManager.types";

// Re-export för bekvämlighet i komponenterna. Enums exporteras som värden
// (inte `export type`) eftersom de används vid runtime.
export type {
  ActivityCounts,
  ActivityTrend,
  Award,
  CategoryBreakdown,
  FunFacts,
  SalesManagerFilterParams,
  SalesOverviewStats,
  SalespersonActivityStats,
  SalesTeamSummary,
  SegmentBreakdown,
  SegmentKey,
  SubtypeBreakdown,
  WorkItemSummary,
} from "../types/salesManager.types";
export {
  CustomerSegment,
  WorkItemCategory,
  WorkItemSubtype,
} from "../types/salesManager.types";

// Bygger querystring enligt backend-kontraktet. `userNos` ska skickas som
// kommaseparerad sträng i en enda query-param: ?userNos=96035,190331
// (enligt backend-handoffen). Tom/utelämnad = alla säljare.
const buildUrl = (
  base: string,
  params: SalesManagerFilterParams,
  includeState: boolean
): string => {
  const qs = new URLSearchParams();
  if (params.fromDate) qs.append("fromDate", params.fromDate);
  if (params.toDate) qs.append("toDate", params.toDate);
  if (params.userNos && params.userNos.length > 0) {
    qs.append("userNos", params.userNos.join(","));
  }
  if (includeState && params.state !== undefined) {
    qs.append("state", params.state.toString());
  }
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
};

// Cachar svaren i minst 30 sekunder — vid remount eller byte till en redan
// sedd argumentuppsättning används cachen direkt utan nätverksanrop om datan
// är färskare än så. Explicit refetch via refetch()-knappen kringgår detta.
const CACHE_SECONDS = 30;

export const salesManagerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Aktivitetsstatistik per säljare. Sorterad fallande på
    // Breakdown.Total.Total.Total.CompletionRate av backend.
    getSalespersonActivityStats: builder.query<
      SalespersonActivityStats[],
      SalesManagerFilterParams
    >({
      query: (params = {}) => ({
        url: buildUrl(
          "/v2/crm/sales-manager/salesperson-stats",
          params,
          true
        ),
        method: "GET",
      }),
      providesTags: ["SalesManagerData", "WorkItem"],
      keepUnusedDataFor: CACHE_SECONDS,
    }),

    // Aggregerad team-överblick över valda säljare
    getSalesTeamSummary: builder.query<
      SalesTeamSummary,
      SalesManagerFilterParams
    >({
      query: (params = {}) => ({
        url: buildUrl(
          "/v2/crm/sales-manager/sales-team-summary",
          params,
          true
        ),
        method: "GET",
      }),
      providesTags: ["SalesManagerData", "Users"],
      keepUnusedDataFor: CACHE_SECONDS,
    }),

    // KPI-snapshot för overview-kort (ersätter tidigare klientsideberäkning)
    getSalesOverview: builder.query<
      SalesOverviewStats,
      SalesManagerFilterParams
    >({
      query: (params = {}) => ({
        url: buildUrl("/v2/crm/sales-manager/overview", params, false),
        method: "GET",
      }),
      providesTags: ["SalesManagerData"],
      keepUnusedDataFor: CACHE_SECONDS,
    }),
  }),
});

export const {
  useGetSalespersonActivityStatsQuery,
  useGetSalesTeamSummaryQuery,
  useGetSalesOverviewQuery,
} = salesManagerApi;
